import { Eye, Edit, Trash2 } from 'lucide-react';

/**
 * MovimientoRow - Componente de fila visual para mostrar movimientos
 * con badges de estado, importe formateado y acciones
 * 
 * @param {Object} props
 * @param {Object} props.movimiento - Datos del movimiento
 * @param {Function} props.onView - Callback al ver
 * @param {Function} props.onEdit - Callback al editar
 * @param {Function} props.onDelete - Callback al eliminar
 * @param {'pago'|'cobranza'} props.tipo - Tipo de movimiento
 */
export default function MovimientoRow({
  movimiento,
  onView,
  onEdit,
  onDelete,
  tipo = 'pago',
}) {
  const formatCurrency = (value, moneda = 'ARS') => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: moneda,
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getEstadoBadge = (estado) => {
    const styles = {
      PENDIENTE: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      PARCIAL: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      CERRADO: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    };

    const labels = {
      PENDIENTE: 'Pendiente',
      PARCIAL: 'Parcial',
      CERRADO: 'Cerrado',
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          styles[estado] || styles.PENDIENTE
        }`}
      >
        {labels[estado] || estado}
      </span>
    );
  };

  const entidad = tipo === 'pago' ? movimiento.proveedor : movimiento.cliente;

  return (
    <tr className="hover:bg-[rgba(var(--muted),0.2)] transition-colors">
      {/* Fecha */}
      <td className="px-4 py-3 text-sm text-[rgb(var(--fg))]">
        {formatDate(movimiento.fecha)}
      </td>

      {/* Entidad (Cliente/Proveedor) */}
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-[rgb(var(--fg))]">
            {entidad?.nombre || 'N/A'}
          </span>
          {entidad?.cuit && (
            <span className="text-xs text-[rgb(var(--muted))]">{entidad.cuit}</span>
          )}
        </div>
      </td>

      {/* Medio de Pago */}
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <span className="text-sm text-[rgb(var(--fg))]">
            {movimiento.medioPago?.nombre || 'N/A'}
          </span>
          {movimiento.cuentaBancaria && (
            <span className="text-xs text-[rgb(var(--muted))]">
              {movimiento.cuentaBancaria.banco}
            </span>
          )}
        </div>
      </td>

      {/* Importe */}
      <td className="px-4 py-3 text-sm font-semibold text-right text-[rgb(var(--fg))]">
        {formatCurrency(movimiento.importe, movimiento.moneda)}
      </td>

      {/* Moneda */}
      <td className="px-4 py-3">
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[rgba(var(--brand),0.1)] text-[rgb(var(--brand))]">
          {movimiento.moneda || 'ARS'}
        </span>
      </td>

      {/* Estado */}
      <td className="px-4 py-3">{getEstadoBadge(movimiento.estado)}</td>

      {/* Acciones */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onView(movimiento)}
            className="p-1.5 rounded-lg hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            aria-label="Ver detalles"
            title="Ver detalles"
          >
            <Eye className="w-4 h-4" aria-hidden="true" />
          </button>
          <button
            onClick={() => onEdit(movimiento)}
            className="p-1.5 rounded-lg hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-[rgb(var(--brand))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            aria-label="Editar"
            title="Editar"
          >
            <Edit className="w-4 h-4" aria-hidden="true" />
          </button>
          <button
            onClick={() => onDelete(movimiento)}
            className="p-1.5 rounded-lg hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-red-600 dark:hover:text-red-400 transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            aria-label="Eliminar"
            title="Eliminar"
          >
            <Trash2 className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </td>
    </tr>
  );
}
